import { ActivatedRoute, Router } from '@angular/router';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of, switchMap, Subject } from 'rxjs';
import {
    catchError,
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    withLatestFrom,
} from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { IntegrationsService } from '@app/integrations/services/integrations.service';
import { WinmoService } from '@app/integrations/winmo/services/winmo.service';
import { INTEGRATION } from '@app/integrations/integrations.types';
import { REPORT_CONFIG } from '@shared/const/report-config';
import { ReportConfig } from '@shared/interfaces/ReportConfig';
import { FiltersService } from '@shared/services/filters/filters.service';
import { AnalyticsService, CurrencyService } from '@shared/services';
import { ReportSettingsService } from '@shared/services/report-settings.service';
import { IReportComparisonSettings } from '@shared/interfaces/IReportSettings';
import { WidgetInfo } from '@shared/widgets/core/widgets.types';
import { CommonHelpers } from '@shared/helpers/common.helpers';
import { AnalyticsData, ExportItem } from '@shared/components/common-export-button/common-export-button.types';
import { EntityHelpers } from '@main/helpers/entity.helpers';
import { NavigationService } from '@main/services/navigation.service';
import { ViewTokenParams } from '@main/services/view-report.service.types';
import { EntityGroup, EntityItem, ReportType } from '../models';
import { BUSINESS_ENTITY, WIDGET } from '../enums';
import { IEntityItemId } from '../types';
import {
    ReportUpdateTriggerResult,
    WidgetStatus,
    WidgetTriggerUpdateOptions,
    WidgetTriggerUpdateResult,
} from '../services/report.service.types';
import { ViewReportService } from '../services/view-report.service';
import { FeatureToggleService } from '@main/shared/feature-toggle';
import { ReportHelpers } from './report.helpers';
import { ReportServiceBase } from './report.service-base';
import { ReportDataAggregationService } from '../services/report-data-aggregation.service';
import { ReportDeviationData } from './report.types';

@UntilDestroy()
@Injectable()
export class ReportService extends ReportServiceBase {
    rootEntity$ = new BehaviorSubject<EntityGroup>(null);
    entities$ = new BehaviorSubject<EntityItem[]>([]);

    resetScrollbar$ = new Subject<void>();

    categoryBenchmark: boolean;
    categoryBenchmarkId: IEntityItemId;

    reportReady$ = combineLatest([this.rootEntity$, this.filtersService.ready$]).pipe(
        map(([rootEntity, filtersReady]) => Boolean(rootEntity) && filtersReady),
        shareReplay(1),
    );

    widgets: WidgetInfo[];
    widgetsStatus$ = new BehaviorSubject<WidgetStatus[]>([]);
    deviation$: Observable<ReportDeviationData>;

    get isViewTokenValid(): boolean {
        return Boolean(this.viewReportService.viewToken$.value);
    }

    get rootEntity(): EntityGroup {
        return this.rootEntity$.value;
    }

    get entities(): EntityItem[] {
        return this.entities$.value;
    }

    get fullScreenViewEntityId(): IEntityItemId {
        const entityId = this.navigationService.getQueryParam('entityId');

        return Number(entityId) || entityId;
    }

    get isFullscreenEntityMatchBenchmarkEntity(): boolean {
        if (!this.fullScreenViewEntityId || !this.categoryBenchmarkId) return false;

        return this.fullScreenViewEntityId === Number(this.categoryBenchmarkId);
    }

    get fullscreenEntity() {
        const entityType = this.isFullscreenEntityMatchBenchmarkEntity ? BUSINESS_ENTITY.Category : null;
        const entityId = this.fullScreenViewEntityId;

        return EntityHelpers.pickEntityById(this.entities, entityId, entityType);
    }

    get analyticsReportType(): ReportType {
        if (this.rootEntity.items.length === 1) return ReportType.SINGLE;
        return this.rootEntity?.isComparison() ? ReportType.COMPARISON : ReportType.GROUP;
    }

    reportUpdateTrigger$: Observable<ReportUpdateTriggerResult>;
    reportTokenUpdateTrigger$: Observable<string>;

    constructor(
        private integrationsService: IntegrationsService,
        private analyticsService: AnalyticsService,
        private filtersService: FiltersService,
        private currencyService: CurrencyService,
        private reportSettingsService: ReportSettingsService,
        private reportDataAggregationService: ReportDataAggregationService,
        private navigationService: NavigationService,
        private viewReportService: ViewReportService,
        private featureService: FeatureToggleService,
        private router: Router,
        @Inject(REPORT_CONFIG) private reportConfig: ReportConfig,
    ) {
        super();

        this.reportUpdateTrigger$ = combineLatest([this.entities$, this.filtersService.value$]).pipe(
            filter(() => !!this.entities.length),
            debounceTime(0),
            map(([, filters]) => ({ filters })),
            shareReplay(1),
        );

        this.reportTokenUpdateTrigger$ = this.reportUpdateTrigger$.pipe(
            switchMap(({ filters }) =>
                this.viewReportService
                    .updateToken({
                        entityType: this.reportConfig.entityType,
                        compare: this.reportConfig.comparison,
                        entities: [this.rootEntity],
                        filters,
                        categoryBenchmarkId: this.categoryBenchmarkId,
                    })
                    .pipe(
                        catchError((err) => {
                            if (err.status === 400) {
                                this.router.navigate(['bad-request']);
                            }

                            return of(null);
                        }),
                    ),
            ),
            shareReplay(1),
        );

        this.reportTokenUpdateTrigger$
            .pipe(
                untilDestroyed(this),
                filter((token) => Boolean(token)),
                withLatestFrom(this.reportUpdateTrigger$),
                map(([, triggers]) => triggers),
                distinctUntilChanged(
                    ({ filters: filtersPrev }, { filters: filtersCurr }) =>
                        filtersPrev.country.id === filtersCurr.country.id,
                ),
                filter(() => !this.isWinmoSneakView()),
            )
            .subscribe(({ filters }) => {
                this.trackReportViewChange({
                    entityType: this.reportConfig.entityType,
                    compare: this.reportConfig.comparison,
                    entities: this.entities,
                    filters,
                    categoryBenchmarkId: this.categoryBenchmarkId,
                });
            });

        this.deviation$ = this.reportTokenUpdateTrigger$.pipe(
            filter(Boolean),
            withLatestFrom(this.reportUpdateTrigger$, this.currencyService.currency$),
            switchMap(([, { filters }, currency]) =>
                this.reportDataAggregationService.fetchDataDeviation(
                    this.entities,
                    filters,
                    currency,
                    this.rootEntity.entityType,
                    'widget_month_over_month' as WIDGET,
                ),
            ),
            map((data) => ({
                data,
                impressions: Boolean(data?.deviation?.impressions),
                expenses: Boolean(data?.deviation?.expenses),
            })),
            shareReplay(1),
        );
    }

    getWidgetUpdateTrigger(
        widgetId: string,
        options: WidgetTriggerUpdateOptions = {} as WidgetTriggerUpdateOptions,
    ): Observable<WidgetTriggerUpdateResult> {
        const widget = this.widgets.find((v) => v.id === widgetId);

        return combineLatest([
            this.reportTokenUpdateTrigger$,
            options.comparisonSettings ? this.reportSettingsService.comparison$ : of<IReportComparisonSettings>(null),
        ]).pipe(
            filter(() => Boolean(this.viewReportService.viewToken)),
            delay(10),
            withLatestFrom(this.reportUpdateTrigger$, this.currencyService.currency$),
            map(([[, comparisonSettings], { filters }, currency]) => ({
                entityType: this.reportConfig.entityType,
                entities: this.entities,
                filters,
                currency,
                reportSettings: {
                    comparison: comparisonSettings,
                },
                deviation$: widget?.config?.showDeviation
                    ? this.deviation$
                    : of({ data: null, impressions: false, expenses: false }),
            })),
            shareReplay(1),
        );
    }

    checkForCategoryBenchmarkRoute(route: ActivatedRoute): void {
        const {
            queryParams: { category },
        } = route.snapshot;

        if (this.reportConfig.comparison && category) {
            if (this.featureService.isEnabled('category_report_benchmark')) {
                this.setCategoryBenchmark(category);
            } else {
                this.router.navigate(['not-found']);
            }
        }
    }

    setRootEntity(entity: EntityGroup): void {
        this.rootEntity$.next(entity);
        this.entities$.next(entity.items);

        if (entity.isCategoryBenchmarkComparison()) {
            const categoryEntity = entity.items.find((v) => v.entityType === BUSINESS_ENTITY.Category);

            this.setCategoryBenchmark(categoryEntity?.id);
        }
    }

    reset(): void {
        this.viewReportService.reset();

        this.rootEntity$.next(null);
        this.entities$.next([]);
    }

    getGlobalExportFileNameTpl(): string {
        let entities = this.entities;

        if (this.categoryBenchmark) entities = entities.filter(({ id }) => id !== this.categoryBenchmarkId);

        let groupTypePrefix: string;

        if (this.categoryBenchmark) groupTypePrefix = 'benchmark_comparison';
        else {
            groupTypePrefix = (entities.length > 1 && (this.reportConfig.comparison ? 'comparison' : 'group')) || '';
        }

        const entitiesTitles = ReportHelpers.serializeExportEntitiesFileNamePart(
            this.rootEntity,
            entities,
            this.categoryBenchmarkId,
        );

        return CommonHelpers.templateFileName(this.reportConfig.globalExportFileName, {
            groupType: groupTypePrefix,
            entities: entitiesTitles,
        });
    }

    getWidgetExportFileNameTpl(widgetName: string, entities: EntityItem[]): string {
        const { categoryBenchmark } = this;

        const fileName = CommonHelpers.templateFileName(this.reportConfig.widgetExportFileName, {
            widgetName,
            entities: this.getEntitiesFileNameTpl(entities),
        });

        return categoryBenchmark ? `benchmark_${fileName}` : fileName;
    }

    getEntitiesFileNameTpl(entities: EntityItem[]): string {
        const { rootEntity, categoryBenchmark, categoryBenchmarkId } = this;

        if (categoryBenchmark) entities = entities.filter(({ id }) => id !== categoryBenchmarkId);

        return ReportHelpers.serializeExportEntitiesFileNamePart(rootEntity, entities, categoryBenchmarkId);
    }

    trackExportAction(item: ExportItem, analyticsData: AnalyticsData): void {
        const { rootEntity, entities, categoryBenchmark } = this;
        const { country$, period$, channel$, device$ } = this.filtersService;

        this.analyticsService.trackBusinessAction('Export', {
            'Entity Type': this.analyticsService.getAnalyticsEntityType(rootEntity, categoryBenchmark),
            'Report Type':
                entities.length === 1
                    ? ReportType.SINGLE
                    : rootEntity?.isComparison()
                    ? ReportType.COMPARISON
                    : ReportType.GROUP,
            Country: country$.getValue()?.title,
            Period: period$.getValue()?.title,
            Channel: channel$.getValue()?.title,
            Device: device$.getValue()?.title,
            Format: item.id,
            ...analyticsData,
        });
    }

    private trackReportViewChange(params: ViewTokenParams) {
        const { filters } = params;
        const { channel, device, country, period } = filters;
        const entities = params.entities.map(({ title }) => title);

        this.analyticsService.trackBusinessAction('[Security] Report View', {
            'Entity Type': this.analyticsService.getAnalyticsEntityType(this.rootEntity, this.categoryBenchmark),
            'Report Type': this.analyticsReportType,
            Entities: entities,
            'Entities Count': entities.length,
            Country: country?.title,
            Period: period?.title,
            Channel: channel?.title,
            Device: device?.title,
        });
    }

    private setCategoryBenchmark(categoryId: IEntityItemId): void {
        this.categoryBenchmarkId = (categoryId && Number(categoryId)) || null;
        this.categoryBenchmark = Boolean(this.categoryBenchmarkId);
    }

    private isWinmoSneakView(): boolean {
        return (
            this.integrationsService.isIntegration(INTEGRATION.WINMO) &&
            this.integrationsService.getService<WinmoService>().isSneakPeekView()
        );
    }
}
